import {
  ActionMenu,
  Button,
  Field,
  Icon,
  Input,
  Modal,
  Stack,
  useActions,
  useTheme
} from "@introist/react-foundation/v2";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../services/rpc/RpcProvider";

export const EmployeeActions = ({ employeeId }: { employeeId: string }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { onConfirmAction } = useActions();
  const apiUtils = api.useContext();

  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeReason, setFreezeReason] = useState("");

  const deleteOp = api.employees.v4.delete.useMutation({});
  const freezeOp = api.employees.v4.freezeAutomations.useMutation({});
  const unfreezeOp = api.employees.v4.unfreezeAutomations.useMutation({});

  const { data: freeze } = api.employees.v4.automationFreeze.useQuery({ id: employeeId });

  const refreshFreeze = () => apiUtils.employees.v4.automationFreeze.invalidate({ id: employeeId });

  const onDeleteEmployee = onConfirmAction(
    async () => {
      await deleteOp.mutateAsync({ id: employeeId });
      navigate("/employees");
    },
    {
      title: "Delete employee",
      description:
        "Are you sure you want to delete this employee?\nThis will not affect to any active workflows.",
      confirmTitle: "Delete"
    }
  );

  const onFreeze = async () => {
    await freezeOp.mutateAsync({ id: employeeId, reason: freezeReason || null });
    await refreshFreeze();
    setFreezeOpen(false);
    setFreezeReason("");
  };

  const onUnfreeze = onConfirmAction(
    async () => {
      await unfreezeOp.mutateAsync({ id: employeeId });
      await refreshFreeze();
    },
    {
      title: "Unfreeze automations",
      description:
        "New automations can start for this employee again.\nTriggers whose rules match this employee will start automations.",
      confirmTitle: "Unfreeze"
    }
  );

  return (
    <>
      <ActionMenu
        options={[
          freeze
            ? {
                key: "unfreeze",
                startAdornment: <Icon name="playCircle" />,
                title: "Unfreeze automations",
                onClick: onUnfreeze
              }
            : {
                key: "freeze",
                startAdornment: <Icon name="pause" />,
                title: "Freeze automations",
                onClick: () => setFreezeOpen(true)
              },
          {
            key: "delete",
            startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
            title: "Delete employee",
            onClick: onDeleteEmployee,
            titleStyles: { color: theme.palette.danger.default }
          }
        ]}
      />
      <Modal open={freezeOpen} onClose={() => setFreezeOpen(false)} title="Freeze automations">
        <Stack vertical>
          <span style={{ maxWidth: "400px" }}>
            No new automations will start for this employee from any source. Running automations are
            not affected.
          </span>
          <Field title="Reason (optional)">
            <Input size="small" autoFocus value={freezeReason} onChange={setFreezeReason} />
          </Field>
          <Stack justifyContent="flex-end">
            <Button onClickWithLoading={onFreeze}>Freeze</Button>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};
